/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import {
  ACTIVITY,
  DASHBOARD,
  DEVICES,
  EDGES,
  NETWORKS,
  RADIOS,
  SETTINGS,
  SIMULATORS,
  VMZ,
  HARDWARE_INVENTORY,
  PROFILES,
  IT_DASHBOARD,
  DAILY_REPORT,
  ONBOARDING,
  CREATE_SITE,
  CREATE_VMZ,
  CREATE_RADIO_GROUP,
  CREATE_VMZ_GROUP,
  CREATE_APPLICATION,
  CREATE_DEVICE_GROUP,
  DEVICE_ADMINISTRATION,
  CREATE_NETWORK,
  DEPLOY_EDGE,
  CONNECT_NETWORKS_TO_EDGE,
  VMC_URL,
  USERS,
} from "../constants/routes";
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { CircularProgress } from '@h9/ui-lib';
import onAppStart from "./onAppStart";
import Layout from "./Layout";
import LoginExpiryAlert from "~/components/shared/LoginExpiryAlert";
import { useAppSelector } from "~/store/hooks";
import { selectUserPermssions, selectUserPermssionsLoading } from "~/store/context/authSlice";
import { settingsAirgapEnabled, settingsLoading, settingsSystemDashboardConsoleEnabled, settingsSystemOnboardingEnabled } from "~/store/context/settingsSlice";
import { authenticationService } from "~/services";
import { useInterval } from "~/hooks/useInterval";
import evt from "~/store/event";
import { isPartnerKpiOnlyUser } from "~/helpers/partnerKpiOnly";

const DefaultLandingRedirect = lazy(() => import("./DefaultLandingRedirect"));
const Dashboard = lazy(() => import("../views/dashboard/Dashboard"));
const RadiosPage = lazy(() => import("../views/radios/RadiosPage"));
const EdgesPage = lazy(() => import("../views/edges/EdgesPage"));
const DevicePage = lazy(() => import("../views/subscribers/DevicePage"));
const DnnPanel = lazy(() => import("../views/dnn/DnnPanel"));
const VMZone = lazy(() => import("../views/vmz"));
const Diagnostics = lazy(() => import("../views/diagnostics/SimulatorPanel"));
const Activity = lazy(() => import("../views/activity/index"));
const AdminPage = lazy(() => import("../views/adminstration/AdminPage"));
const UsersPage = lazy(() => import("../views/users/UsersPage"));
const NotImplemented = lazy(() => import("../views/NotImplemented/NotImplemented"));
const HardwareInventoryPanel = lazy(() => import("../views/hardwareInventory/HarwareInventoryPanel"));
const Profiles = lazy(() => import("../views/profiles/ProfilePanel"));
const CreateSite = lazy(() => import("../views/radios/Sites/Form"));
const ITDashboard = lazy(() => import("../views/ITDashboard"));
const Onboarding = lazy(() => import("../views/Onboarding/LandingPage"));
const CreateVmz = lazy(() => import("../views/vmz/zone/CreateVmz"));
const CreateRadioGroup = lazy(() => import("../views/radios/RadioGroup/Form"));
const CreateVmzGroup = lazy(() => import("../views/Onboarding/CreateVmzGroup"));
const CreateApplication = lazy(() => import("../views/Onboarding/CreateApplication"));
const CreateDeviceGroup = lazy(() => import("../views/subscribers/deviceGroups/CreateDeviceGroup"));
const DeviceAdministration = lazy(() => import("../views/subscribers/deviceGroups/DeviceAdministration"));
const CreateNetwork = lazy(() => import("../views/dnn/CreateNetwork"));
const DeployEdge = lazy(() => import("../views/Onboarding/DeployEdge"));
const ConnectNetworksToEdge = lazy(() => import("../views/Onboarding/ConnectNetworksToEdge"));

type LState = {
  fromChat?: boolean;
} | undefined;

type Props = {};
const AppRoutes = (_props: Props) => {
  /**
   * Runs the onAppStart function when the component mounts.
   * This effect is responsible for performing any necessary setup or initialization tasks when the app starts.
   */
  useEffect(() => {
    onAppStart();
  }, []);

  useInterval(() => { evt.emit('refreshServiceController') }, 60000);

  const permissionsMap = useAppSelector(selectUserPermssions);
  const permissionsLoading = useAppSelector(selectUserPermssionsLoading);
  const onboardingEnabled = useAppSelector(settingsSystemOnboardingEnabled);
  const monitorPageEnabled = useAppSelector(settingsSystemDashboardConsoleEnabled);
  const systemSettingsLoading = useAppSelector(settingsLoading);

  const history = useLocation<LState>();
  const [loading, setLoading] = useState(false);

  const isKpiOnlyUser = isPartnerKpiOnlyUser();

  const showOnboarding = useMemo(() => {
    if (isKpiOnlyUser) return false;
    return !systemSettingsLoading && !permissionsLoading && onboardingEnabled && (
      // At least on of the view permission should exist for the Getting Started page to be visible
      permissionsMap.radioSite?.VIEW || permissionsMap.radioGroup?.VIEW || permissionsMap.dnn?.VIEW || permissionsMap.deviceGroup?.VIEW || permissionsMap.zone?.VIEW || permissionsMap.edge?.VIEW || permissionsMap.device?.VIEW
    )
  }, [isKpiOnlyUser, onboardingEnabled, systemSettingsLoading, permissionsLoading, permissionsMap]);

  const showMonitorPage = useMemo(() => !isKpiOnlyUser && !systemSettingsLoading && !permissionsLoading && permissionsMap.itAdminDashboard?.VIEW && monitorPageEnabled, [isKpiOnlyUser, systemSettingsLoading, permissionsLoading, permissionsMap.itAdminDashboard?.VIEW, monitorPageEnabled]);

  const airgapEnabled = useAppSelector(settingsAirgapEnabled);
  const isOperator = authenticationService.isOperator();
  const showUsersPage = (airgapEnabled && permissionsMap.user?.VIEW) || isOperator;

  useEffect(() => {
    if (history.state?.fromChat) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }, [history]);

  return (
    <Layout>
        <Suspense fallback={<Loading />}>
        { loading && <Loading overlay /> }
        <Switch>
          <Route exact path={VMC_URL} component={DefaultLandingRedirect} />
          <Route exact path={DASHBOARD} component={Dashboard} />
          { permissionsMap.radio?.VIEW && <Route exact path={RADIOS} component={RadiosPage} />}
          { permissionsMap.edge?.VIEW && permissionsMap.dnn?.VIEW && <Route exact path={EDGES} component={EdgesPage} />}
          { permissionsMap.device?.VIEW && <Route exact path={DEVICES} component={DevicePage} />}
          { permissionsMap.dnn?.VIEW && <Route exact path={NETWORKS} component={DnnPanel} />}
          { permissionsMap.zone?.VIEW && !isKpiOnlyUser && <Route exact path={VMZ} component={VMZone} />}
          { permissionsMap.simulator?.VIEW && !isKpiOnlyUser && <Route exact path={SIMULATORS} component={Diagnostics} />}
          { permissionsMap.network?.VIEW && !isKpiOnlyUser && <Route exact path={SETTINGS} component={AdminPage} />}
          { showUsersPage && <Route exact path={USERS} component={UsersPage} />}
          { permissionsMap.mobilityProfile?.VIEW && permissionsMap.systemSettings?.VIEW && !isKpiOnlyUser && <Route exact path={PROFILES} component={Profiles} />}
          { permissionsMap.log?.VIEW && <Route exact path={ACTIVITY} component={Activity} />}
          { permissionsMap.vmcHardware?.VIEW && !isKpiOnlyUser && <Route exact path={HARDWARE_INVENTORY} component={HardwareInventoryPanel} />}
          { showMonitorPage && <Route exact path={IT_DASHBOARD} component={ITDashboard} />}
          {/* The report moved into Monitor's "VMC Report" tab; keep old links working. */}
          <Redirect exact from={DAILY_REPORT} to={`${IT_DASHBOARD}?tab=dailyReport`} />
          { showOnboarding &&
            <Route path={ONBOARDING}>
              <Switch>
                {/* CreateSiteFlow */}
                { permissionsMap.radioSite?.ADD && <Route exact path={CREATE_SITE} component={CreateSite} />}
                {/* CreateVMZFlow */}
                { permissionsMap.zone?.ADD && <Route exact path={CREATE_VMZ} component={CreateVmz} />}
                {/* CreateVMZGroupFlow */}
                { permissionsMap.zoneGroup?.ADD && <Route exact path={CREATE_VMZ_GROUP} component={CreateVmzGroup} />}
                {/* CreateApplicationFlow */}
                { permissionsMap.application?.ADD && <Route exact path={CREATE_APPLICATION} component={CreateApplication} />}
                {/* CreateRadioGroupFlow */}
                { permissionsMap.radioGroup?.ADD && <Route exact path={CREATE_RADIO_GROUP} component={CreateRadioGroup} />}
                {/* CreateNetworkFlow */}
                { permissionsMap.dnn?.ADD && <Route exact path={CREATE_NETWORK} component={CreateNetwork} />}
                {/* CreateDeviceGroupFlow */}
                { permissionsMap.deviceGroup?.ADD && <Route exact path={CREATE_DEVICE_GROUP} component={CreateDeviceGroup} />}
                {/* Device Administration */}
                { permissionsMap.device?.EDIT && <Route exact path={DEVICE_ADMINISTRATION} component={DeviceAdministration} />}
                {/* DeployEdgeFlow */}
                { permissionsMap.edge?.ADD && <Route exact path={DEPLOY_EDGE} component={DeployEdge} />}
                {/* ConnectNetworksToEdgeFlow */}
                { permissionsMap.edge?.EDIT && <Route exact path={CONNECT_NETWORKS_TO_EDGE} component={ConnectNetworksToEdge} />}
                {/* Default onboarding view */}
                <Route path={ONBOARDING} component={Onboarding} />
              </Switch>
            </Route>
          }
          { (permissionsLoading || systemSettingsLoading) && <Route component={Loading} />}
          {isKpiOnlyUser ? <Route component={() => <Redirect to={DASHBOARD} />} /> : <Route component={NotImplemented} />}
        </Switch>
      </Suspense>
      <LoginExpiryAlert />
    </Layout>
  );
};

export default AppRoutes;

type LoadingProps = {
  overlay?: boolean;
};

export function Loading({ overlay }: LoadingProps) {
  return (
    <div
      style={{
        position: overlay ? "absolute" : undefined,
        top: 0,
        left: 0,
        backdropFilter: "blur(3px)",
        zIndex: 1000,

        display: "flex",
        width: overlay ? "80vw" : "100%",
        height: "100%",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <CircularProgress color="primary" />
    </div>
  );
}
